// strings.ts — locale dictionary for the par/win panel.
//
// The I18N helper is currently defined inline in index.html, which I don't
// have, so this is a standalone module: either import it there and feed it to
// the existing helper, or paste the object in. Nothing else in the codebase
// changes shape because of it.
//
// A note on the keys, because the sample English string and the code that
// fills it disagreed:
//
//   {t} is the COST improvement as a bare percentage magnitude — "30", not
//       "30%" and not "-30". The percent sign and any spacing before it live
//       in the translated template, because that spacing is a per-locale
//       typographic rule (see below) and cannot be appended generically.
//   {c} is the signed absolute cost delta in M$ as produced by fmtDelta,
//       e.g. "\u221212.3". Unused by these strings; kept available.
//
// Percent-sign spacing, which is why the templates carry their own "%":
//   de / fr / es  -> space before the sign (DIN 5008; French typography;
//                    RAE recommendation). French uses U+202F NARROW NO-BREAK
//                    SPACE so the number and sign never wrap apart.
//   en / it / zh / ja -> no space.
//
// Language tags are BCP 47: "ja", not "JP" (JP is the *country* subtag), and
// "zh-Hans" for Simplified. If the existing helper keys off "JP"/"ZH" it will
// miss these; normalise on the way in.

export type ParStrings = {
  par_win_body: string;
  par_status_win: string;
  par_status_partial: string;
  par_status_behind: string;
  hint_fall_line_snap: string;
};

// Key names are given as the label PRINTED ON THE KEY in each locale, not
// transliterated: French AZERTY reads "Maj", Spanish "Mayús", Italian
// "Maiusc", German "Umschalt". Telling a French player to hold "Shift" sends
// them looking for a key their keyboard does not have. F is F everywhere in
// Latin-script layouts, and CJK keyboards use the US labels, so Shift stays
// Shift in zh and ja.

export const PAR_STRINGS: Record<string, ParStrings> = {
  en: {
    par_win_body:
      "Your road beats the existing route on speed and has a {t}% lower cost.",
    par_status_win: "Ahead of target on cost and time",
    par_status_partial: "Ahead of target on one aspect, behind on the other",
    par_status_behind: "Behind target on both cost and time",
    hint_fall_line_snap:
      "Press F to set the road's direction at the last control point you touched perpendicular to the fall line. Hold Shift while dragging a handle to keep its angle and change only its length.",
  },

  de: {
    par_win_body:
      "Deine Stra\u00dfe ist schneller als die bestehende Route und verursacht {t}\u00a0% geringere Kosten.",
    par_status_win: "Bei Kosten und Zeit besser als das Ziel",
    par_status_partial:
      "In einem Punkt besser als das Ziel, im anderen schlechter",
    par_status_behind: "Bei Kosten und Zeit schlechter als das Ziel",
    hint_fall_line_snap:
      "Dr\u00fccke F, um die Stra\u00dfenrichtung am zuletzt bearbeiteten Kontrollpunkt senkrecht zur Falllinie auszurichten. Halte Umschalt gedr\u00fcckt, w\u00e4hrend du einen Griff ziehst, um dessen Winkel beizubehalten und nur die L\u00e4nge zu \u00e4ndern."
  },

  fr: {
    par_win_body:
      "Votre route est plus rapide que l\u2019itin\u00e9raire existant et co\u00fbte {t}\u202f% de moins.",
    par_status_win: "Meilleur que l\u2019objectif en co\u00fbt et en temps",
    par_status_partial:
      "Meilleur que l\u2019objectif sur un crit\u00e8re, moins bon sur l\u2019autre",
    par_status_behind: "Moins bon que l\u2019objectif en co\u00fbt et en temps",
    hint_fall_line_snap:
      "Appuyez sur F pour orienter la route au dernier point de contr\u00f4le modifi\u00e9 perpendiculairement \u00e0 la ligne de plus grande pente. Maintenez Maj enfonc\u00e9e en d\u00e9pla\u00e7ant une poign\u00e9e pour conserver son angle et n\u2019en modifier que la longueur."
  },

  it: {
    par_win_body:
      "La tua strada \u00e8 pi\u00f9 veloce del percorso esistente e costa il {t}% in meno.",
    par_status_win: "Meglio dell\u2019obiettivo per costo e tempo",
    par_status_partial:
      "Meglio dell\u2019obiettivo per un aspetto, peggio per l\u2019altro",
    par_status_behind: "Peggio dell\u2019obiettivo sia per costo sia per tempo",
    hint_fall_line_snap:
      "Premi F per orientare la strada nell\u2019ultimo punto di controllo modificato perpendicolarmente alla linea di massima pendenza. Tieni premuto Maiusc mentre trascini una maniglia per mantenerne l\u2019angolo e cambiarne solo la lunghezza."
  },

  es: {
    par_win_body:
      "Tu carretera es m\u00e1s r\u00e1pida que la ruta existente y cuesta un {t}\u00a0% menos.",
    par_status_win: "Mejor que el objetivo en coste y tiempo",
    par_status_partial: "Mejor que el objetivo en un aspecto y peor en el otro",
    par_status_behind: "Peor que el objetivo en coste y tiempo",
    hint_fall_line_snap:
      "Pulsa F para orientar la carretera en el \u00faltimo punto de control modificado perpendicularmente a la l\u00ednea de m\u00e1xima pendiente. Mant\u00e9n pulsada May\u00fas mientras arrastras un manejador para conservar su \u00e1ngulo y cambiar solo su longitud."
  },

  "zh-Hans": {
    par_win_body:
      "\u4f60\u4fee\u5efa\u7684\u9053\u8def\u6bd4\u73b0\u6709\u8def\u7ebf\u66f4\u5feb\uff0c\u6210\u672c\u4e5f\u964d\u4f4e\u4e86 {t}%\u3002",
    par_status_win: "\u6210\u672c\u548c\u65f6\u95f4\u5747\u4f18\u4e8e\u76ee\u6807",
    par_status_partial:
      "\u4e00\u9879\u4f18\u4e8e\u76ee\u6807\uff0c\u53e6\u4e00\u9879\u672a\u8fbe\u6807",
    par_status_behind: "\u6210\u672c\u548c\u65f6\u95f4\u5747\u672a\u8fbe\u76ee\u6807",
    hint_fall_line_snap:
      "\u6309 F \u952e\u53ef\u5c06\u6700\u540e\u4fee\u6539\u7684\u63a7\u5236\u70b9\u5904\u7684\u9053\u8def\u65b9\u5411\u8c03\u6574\u4e3a\u4e0e\u6700\u5927\u5761\u5ea6\u7ebf\u5782\u76f4\u3002\u62d6\u52a8\u63a7\u5236\u67c4\u65f6\u6309\u4f4f Shift \u952e\uff0c\u53ef\u4fdd\u6301\u5176\u89d2\u5ea6\u4e0d\u53d8\uff0c\u4ec5\u6539\u53d8\u957f\u5ea6\u3002"
  },

  ja: {
    par_win_body:
      "\u3042\u306a\u305f\u306e\u9053\u8def\u306f\u65e2\u5b58\u30eb\u30fc\u30c8\u3088\u308a\u901f\u304f\u3001\u30b3\u30b9\u30c8\u3082 {t}% \u4f4e\u304f\u306a\u3063\u3066\u3044\u307e\u3059\u3002",
    par_status_win:
      "\u30b3\u30b9\u30c8\u30fb\u6642\u9593\u3068\u3082\u306b\u76ee\u6a19\u3092\u4e0a\u56de\u3063\u3066\u3044\u307e\u3059",
    par_status_partial:
      "\u4e00\u65b9\u306f\u76ee\u6a19\u3092\u4e0a\u56de\u308a\u3001\u3082\u3046\u4e00\u65b9\u306f\u4e0b\u56de\u3063\u3066\u3044\u307e\u3059",
    par_status_behind:
      "\u30b3\u30b9\u30c8\u30fb\u6642\u9593\u3068\u3082\u306b\u76ee\u6a19\u306b\u5c4a\u3044\u3066\u3044\u307e\u305b\u3093",
    hint_fall_line_snap:
      "F \u30ad\u30fc\u3092\u62bc\u3059\u3068\u3001\u6700\u5f8c\u306b\u64cd\u4f5c\u3057\u305f\u30b3\u30f3\u30c8\u30ed\u30fc\u30eb\u30dd\u30a4\u30f3\u30c8\u3067\u306e\u9053\u8def\u306e\u5411\u304d\u304c\u6700\u5927\u50be\u659c\u7dda\u306b\u5bfe\u3057\u3066\u5782\u76f4\u306b\u306a\u308a\u307e\u3059\u3002\u30cf\u30f3\u30c9\u30eb\u3092\u30c9\u30e9\u30c3\u30b0\u3059\u308b\u969b\u306b Shift \u30ad\u30fc\u3092\u62bc\u3057\u305f\u307e\u307e\u306b\u3059\u308b\u3068\u3001\u89d2\u5ea6\u3092\u4fdd\u3063\u305f\u307e\u307e\u9577\u3055\u3060\u3051\u3092\u5909\u66f4\u3067\u304d\u307e\u3059\u3002"
  },
};

/** Map a browser/UI tag onto a key above. Accepts "JP"/"ZH" spellings too. */
export function resolveLocale(tag: string | undefined): string {
  if (!tag) return "en";
  const s = tag.toLowerCase().replace("_", "-");
  if (s.startsWith("ja") || s === "jp") return "ja";
  if (s.startsWith("zh")) return "zh-Hans";
  for (const k of ["de", "fr", "it", "es"]) if (s.startsWith(k)) return k;
  return "en";
}
